import { useMemo } from 'react'
import { collection, doc, serverTimestamp } from 'firebase/firestore'
import { onSnapshot, setDoc, updateDoc } from '@/lib/metered'
import { db } from '@/lib/firebase'
import { recordChange } from '@/lib/activity'
import { useSharedCollection } from '@/lib/sharedCollection'
import { newActivityId, recordActivity } from '@/features/activity/events'
import { GATES } from '@/data/project'
import { progressFor, type GateState, type GateStatus, type Task, type TaskStatus } from './types'

/**
 * The board.
 *
 * `enabled` mirrors `useDecisions`: the decision register reads the task list to show
 * which roadmap items a decision is holding up, and a role without `roadmap.view` would
 * otherwise get a permission error for a panel it never asked for.
 */
export function useTasks(enabled = true) {
  const { rows, loading, error } = useSharedCollection<Task>(
    'tasks',
    (onRows, onError) => onSnapshot(
      collection(db, 'tasks'),
      (snap) => onRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Task)),
      onError,
    ),
    enabled,
  )
  return { tasks: rows, loading, error }
}

export function useGateStates() {
  const { rows } = useSharedCollection<GateState>(
    'gates',
    (onRows, onError) => onSnapshot(
      collection(db, 'gates'),
      (snap) => onRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as GateState)),
      onError,
    ),
  )
  return useMemo(
    () => Object.fromEntries(rows.map((g) => [g.id, g])) as Record<string, GateState>,
    [rows],
  )
}

export async function setTaskStatus(
  id: string, status: TaskStatus, actor: { email: string; name?: string | null }, from?: TaskStatus,
) {
  await updateDoc(doc(db, 'tasks', id), {
    status, updatedBy: actor.email, updatedAt: serverTimestamp(),
  })
  await recordChange(['tasks', id], { field: 'Status', from: from ?? '', to: status }, actor)
}

export async function setGateStatus(
  id: string, status: GateStatus, actor: { email: string; name?: string | null }, from?: GateStatus,
) {
  await setDoc(
    doc(db, 'gates', id),
    { status, updatedBy: actor.email, updatedAt: serverTimestamp() },
    { merge: true },
  )
  await recordChange(['gates', id], { field: 'Status', from: from ?? '', to: status }, actor)

  // Also into the Activity History. The gate keeps its own change log, which answers
  // "what happened to this gate"; this answers "what happened", across everything, and a
  // gate moving is the whole of a gate's story - it carries no discussion to appear
  // through instead.
  if (from === status) return
  const gate = GATES.find((g) => g.id === id)
  await recordActivity(newActivityId(), {
    kind: 'status', parentKind: 'gate',
    itemId: id, itemRef: id, itemTitle: gate?.name ?? id,
    commentId: '', body: '', from: from ?? '', to: status,
    byEmail: actor.email, byName: actor.name ?? null,
  }).catch(() => undefined)
}

/** Tasks grouped by phase, with progress precomputed. */
export function useTasksByPhase(tasks: Task[]) {
  return useMemo(() => {
    const map = new Map<string, Task[]>()
    for (const t of tasks) {
      const list = map.get(t.phase) ?? []
      list.push(t)
      map.set(t.phase, list)
    }
    // Stable order within a phase: id sorts Milestone before Client and keeps run order.
    for (const list of map.values()) list.sort((a, b) => a.id.localeCompare(b.id))
    return {
      byPhase: map,
      progress: new Map([...map].map(([k, v]) => [k, progressFor(v)])),
    }
  }, [tasks])
}
